import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, FormControlLabel, Switch } from '@mui/material';
import { Add as AddIcon, Cancel as CancelIcon } from '@mui/icons-material';
import FormSelect from './FormSelect.jsx';
import SimpleInput from '../Input/SimpleInput.jsx';

const PrincipalForm = ({ principal, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    ssn: '',
    ownershipPercent: '',
    title: '',
    dateOfBirth: '',
    address1: '',
    zip: '',
    city: '',
    state: '',
    email: '',
    mobilePhone: '',
    driverLicenseNumber: '',
    driverLicenseExpiration: '',
    stateIssued: '',
    managerController: false,
    authorizedSigner: false,
    bankrupt: '',
    bankruptDate: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (principal) {
      setFormData({
        id: principal.id || '',
        firstName: principal.firstName || '',
        lastName: principal.lastName || '',
        ssn: principal.ssn || '',
        ownershipPercent: principal.ownershipPercent || '',
        title: principal.title || '',
        dateOfBirth: principal.dateOfBirth || '',
        address1: principal.address1 || '',
        zip: principal.zip || '',
        city: principal.city || '',
        state: principal.state || '',
        email: principal.email || '',
        mobilePhone: principal.mobilePhone || '',
        driverLicenseNumber: principal.driverLicenseNumber || '',
        driverLicenseExpiration: principal.driverLicenseExpiration || '',
        stateIssued: principal.stateIssued || '',
        managerController: principal.managerController || false,
        authorizedSigner: principal.authorizedSigner || false,
        bankrupt: principal.bankrupt || '',
        bankruptDate: principal.bankruptDate || ''
      });

    }
  }, [principal]);

  const titleOptions = [
    { value: 'owner', label: 'Owner' },
    { value: 'partner', label: 'Partner' },
    { value: 'ceo', label: 'CEO' },
    { value: 'president', label: 'President' },
    { value: 'vice_president', label: 'Vice President' },
    { value: 'director', label: 'Director' },
    { value: 'manager', label: 'Manager' },
    { value: 'other', label: 'Other' }
  ];

  const stateOptions = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'This field is required.';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'This field is required.';
    }

    if (!formData.ssn.trim()) {
      newErrors.ssn = 'This field is required.';
    }

    if (!formData.ownershipPercent || formData.ownershipPercent <= 0) {
      newErrors.ownershipPercent = 'This field is required.';
    }

    if (!formData.title) {
      newErrors.title = 'This field is required.';
    }

    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      const { id, ...dataToSave } = formData; // Exclude id
      onSave(dataToSave);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        {principal ? 'Edit Principal' : 'Add New Principal'}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <Box>
          <SimpleInput
            type="text"
            name="dba_owners[first_name]"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="First Name"
            error={!!errors.firstName}
          />
          {errors.firstName && (
            <Typography variant="caption" sx={{ color: 'red', mt: 0.5, display: 'block' }}>
              {errors.firstName}
            </Typography>
          )}
        </Box>
        <Box>
          <SimpleInput
            type="text"
            name="dba_owners[last_name]"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Last Name"
            error={!!errors.lastName}
          />
          {errors.lastName && (
            <Typography variant="caption" sx={{ color: 'red', mt: 0.5, display: 'block' }}>
              {errors.lastName}
            </Typography>
          )}
        </Box>
        <Box>
          <SimpleInput
            type="text"
            name="dba_owners[ssn]"
            value={formData.ssn}
            onChange={(e) => handleInputChange('ssn', e.target.value)}
            placeholder="SSN"
            error={!!errors.ssn}
          />
          {errors.ssn && (
            <Typography variant="caption" sx={{ color: 'red', mt: 0.5, display: 'block' }}>
              {errors.ssn}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SimpleInput
            type="number"
            name="dba_owners[ownership_percentage]"
            value={formData.ownershipPercent}
            onChange={(e) => handleInputChange('ownershipPercent', e.target.value)}
            placeholder="Ownership %"
            error={!!errors.ownershipPercent}
            sx={{ flex: 1 }}
          />
          {errors.ownershipPercent && (
            <Typography variant="caption" sx={{ color: 'red', mt: 0.5, display: 'block' }}>
              {errors.ownershipPercent}
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: 'grey.600' }}>%</Typography>
        </Box>
        <Box>
          <FormSelect
            label="Title"
            name="dba_owners[designation]"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            options={titleOptions}
            placeholder="Select an option"
            searchable={true}
            error={!!errors.title}
          />
          {errors.title && (
            <Typography variant="caption" sx={{ color: 'red', mt: 0.5, display: 'block' }}>
              {errors.title}
            </Typography>
          )}
        </Box>
        <Box>
          <SimpleInput
            type="date"
            name="dba_owners[doj]"
            value={formData.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
            placeholder="Date of Birth"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <Box>
          <SimpleInput
            type="text"
            name="dba_owners[address]"
            value={formData.address1}
            onChange={(e) => handleInputChange('address1', e.target.value)}
            placeholder="Address"
          />
        </Box>
        <Box>
          <SimpleInput
            type="text"
            name="dba_owners[zip]"
            value={formData.zip}
            onChange={(e) => handleInputChange('zip', e.target.value)}
            placeholder="Zip"
            sx={{ flex: 1 }}
          />
        </Box>
        <Box>
          <SimpleInput
            type="text"
            name="dba_owners[city]"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            placeholder="City"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <Box>
          <FormSelect
            label="State"
            name="dba_owners[state]"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            options={stateOptions}
            placeholder="State"
            searchable={true}
            sx={{ flex: 1 }}
          />
        </Box>
        <Box>
          <SimpleInput
            type="email"
            name="dba_owners[email]"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="E-mail"
          />
        </Box>
        <Box> <SimpleInput
          type="text"
          name="dba_owners[number]"
          value={formData.mobilePhone}
          onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
          placeholder="Mobile Phone"
        /></Box>
      </Box>




      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <SimpleInput
          type="text"
          name="dba_owners[license_no]"
          value={formData.driverLicenseNumber}
          onChange={(e) => handleInputChange('driverLicenseNumber', e.target.value)}
          placeholder="Driver License"
        />
        <SimpleInput
          type="date"
          name="dba_owners[license_expiry_date]"
          value={formData.driverLicenseExpiration}
          onChange={(e) => handleInputChange('driverLicenseExpiration', e.target.value)}
          placeholder="Driver License Expiration"
        />
        <FormSelect
          label="State Issued"
          name="dba_owners[license_state]"
          value={formData.stateIssued}
          onChange={(e) => handleInputChange('stateIssued', e.target.value)}
          options={stateOptions}
          placeholder="Select an option"
          searchable={true}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              name="dba_owners[is_manager]"
              checked={formData.managerController}
              onChange={(e) => handleInputChange('managerController', e.target.checked)}
            />
          }
          label="Manager/Controller"
        />
        <FormControlLabel
          control={
            <Switch
              name="dba_owners[is_authorized]"
              checked={formData.authorizedSigner}
              onChange={(e) => handleInputChange('authorizedSigner', e.target.checked)}
            />
          }
          label="Authorized Signer"
        />
      </Box>

      {/* Bankruptcy Information */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <Box>

        
        <FormSelect
          label="Bankruptcy Type"
          name="dba_owners[bankrupt]"
          value={formData.bankrupt}
          onChange={(e) => handleInputChange('bankrupt', e.target.value)}
          options={[
            { value: 'none', label: 'None' },
            { value: 'personal', label: 'Personal Bankruptcy' },
            { value: 'business', label: 'Business Bankruptcy' },
            { value: 'both', label: 'Both Personal and Business' }
          ]}
          placeholder="Select bankruptcy type"
          searchable={true}
        />
        </Box>
        <Box>
        <SimpleInput
          type="date"
          name="dba_owners[bankrupt_date]"
          value={formData.bankruptDate}
          onChange={(e) => handleInputChange('bankruptDate', e.target.value)}
          placeholder="Bankruptcy Date"
          // label="Bankruptcy Date"
        />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleSave}
          sx={{
            bgcolor: '#1976d2',
            '&:hover': { bgcolor: '#1565c0' },
            px: 3,
            py: 1,
            textTransform: 'none',
          }}
        >
          {principal ? "Update" : "Add"}
        </Button>
        <Button
          variant="contained"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          sx={{
            bgcolor: 'orange.600',
            '&:hover': { bgcolor: 'orange.700' },
            px: 3,
            py: 1,
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default PrincipalForm;